/**
 *  @class
 *  @function ProductSuperzoom
 *
 *  Desktop: hovering the image shows a magnified "zoom box" beside it. Moving
 *  the cursor over the image updates the zoomed detail shown in the box.
 *  Mobile/touch: a long-press on the image activates a zoom overlay on top of
 *  the image; dragging the finger updates the zoomed detail.
 */
if (!customElements.get('product-superzoom')) {
  class ProductSuperzoom extends HTMLElement {
    constructor() {
      super();
      this.zoomSrc = this.dataset.zoomSrc;
      this.naturalWidth = null;
      this.naturalHeight = null;
      this.pane = null;
      this.lens = null;
      this.rect = null;
      this.paneWidth = 0;
      this.paneHeight = 0;
      this.longPressTimer = null;
      this.touchStartPoint = null;
      this.touchActive = false;
      this.repositionQueued = false;
      this.isHovering = false;

      this.onMouseEnter = this.onMouseEnter.bind(this);
      this.onMouseMove = this.onMouseMove.bind(this);
      this.onMouseLeave = this.onMouseLeave.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchMoveWhilePending = this.onTouchMoveWhilePending.bind(this);
      this.onTouchMove = this.onTouchMove.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);
      this.queueReposition = this.queueReposition.bind(this);
    }

    connectedCallback() {
      const image = this.querySelector('img');

      // The variant image-grouping app sets this slide's own image via a raw,
      // unsized file URL, bypassing the theme's responsive width/srcset, so the
      // browser ends up downloading the full-resolution original every time.
      // Re-cap it to the same breakpoints the theme normally serves, and keep
      // watching in case the app swaps the src again later (e.g. color change).
      if (image) {
        const capImageSize = () => {
          const current = image.getAttribute('src');
          if (!current || /[?&]width=/.test(current)) return;
          const sep = current.includes('?') ? '&' : '?';
          const widths = [375, 640, 960, 1280];
          image.setAttribute('srcset', widths.map((w) => `${current}${sep}width=${w} ${w}w`).join(', '));
          image.setAttribute('src', `${current}${sep}width=${widths[widths.length - 1]}`);
        };
        capImageSize();
        new MutationObserver(capImageSize).observe(image, { attributes: true, attributeFilter: ['src'] });
      }

      // Same app-duplication issue as the zoomSrc fix below: on "adapt" aspect ratio,
      // the cloned markup keeps the --padding-bottom computed for the original slide's
      // image, not this slide's actual image, so recompute it from the real <img>.
      if (this.classList.contains('aspect-ratio--adapt') && image) {
        const applyNaturalRatio = () => {
          if (image.naturalWidth && image.naturalHeight) {
            this.style.setProperty('--padding-bottom', (image.naturalHeight / image.naturalWidth * 100) + '%');
          }
        };
        if (image.complete) {
          applyNaturalRatio();
        } else {
          image.addEventListener('load', applyNaturalRatio, { once: true });
        }
      }

      // The variant image-grouping app can duplicate this element's markup and leave a
      // stale data-zoom-src (copied from another slide), so prefer the zoom-link's own
      // href, which always points at this slide's own image, over the dataset value.
      const zoomLink = this.querySelector('.product-single__media-zoom');
      const zoomHref = zoomLink && zoomLink.getAttribute('href');
      if (zoomHref) {
        this.zoomSrc = zoomHref.includes('width=') ? zoomHref : zoomHref + (zoomHref.includes('?') ? '&' : '?') + 'width=2048';
      }

      if (!this.zoomSrc) return;

      this.isCoarsePointer = window.matchMedia('(hover: none), (pointer: coarse)').matches;

      if (this.isCoarsePointer) {
        this.addEventListener('touchstart', this.onTouchStart, { passive: true });
        this.addEventListener('touchend', this.onTouchEnd);
        this.addEventListener('touchcancel', this.onTouchEnd);
      } else {
        this.addEventListener('mouseenter', this.onMouseEnter);
        this.addEventListener('mouseleave', this.onMouseLeave);
      }

      // The variant image-grouping app can hide/replace the whole gallery container
      // (not just this slide) while it's being hovered - e.g. while it swaps the raw
      // gallery for its own processed one a few seconds after load - and no mouseleave
      // ever fires when an ancestor collapses an element like that, so the pane would
      // otherwise stay stuck open forever. A ResizeObserver catches a collapse to zero
      // size regardless of which ancestor/attribute caused it.
      this.visibilityObserver = new ResizeObserver(() => {
        if (this.clientWidth === 0) this.forceHide();
      });
      this.visibilityObserver.observe(this);
    }

    disconnectedCallback() {
      this.teardown();
    }

    forceHide() {
      if (!this.isHovering && !this.touchActive) return;
      this.isHovering = false;
      this.touchActive = false;
      this.removeEventListener('mousemove', this.onMouseMove);
      this.removeEventListener('touchmove', this.onTouchMove);
      window.removeEventListener('scroll', this.queueReposition);
      window.removeEventListener('resize', this.queueReposition);
      if (this.pane) this.pane.classList.remove('is-visible');
      if (this.lens) this.lens.classList.remove('is-visible');
    }

    loadNaturalSize() {
      if (this.naturalWidth) return Promise.resolve();
      if (this.naturalSizePromise) return this.naturalSizePromise;

      this.naturalSizePromise = new Promise((resolve) => {
        const probe = new Image();
        probe.onload = () => {
          this.naturalWidth = probe.naturalWidth;
          this.naturalHeight = probe.naturalHeight;
          resolve();
        };
        probe.onerror = () => resolve();
        probe.src = this.zoomSrc;
      });

      return this.naturalSizePromise;
    }

    ensureLens() {
      if (this.lens) return;
      this.lens = document.createElement('div');
      this.lens.className = 'product-superzoom-lens';
      this.lens.setAttribute('aria-hidden', 'true');
      this.appendChild(this.lens);
    }

    ensurePane(mode) {
      if (this.pane && this.paneMode === mode) return;
      if (this.pane) this.pane.remove();

      this.pane = document.createElement('div');
      this.pane.className = 'product-superzoom-pane';
      this.pane.setAttribute('aria-hidden', 'true');
      this.pane.style.backgroundImage = `url("${this.zoomSrc}")`;
      this.pane.style.backgroundSize = `${this.naturalWidth}px ${this.naturalHeight}px`;

      if (mode === 'overlay') {
        this.pane.classList.add('product-superzoom-pane--overlay');
        this.appendChild(this.pane);
      } else {
        document.body.appendChild(this.pane);
      }

      this.paneMode = mode;
    }

    /* ---------------- Desktop (hover) ---------------- */

    async onMouseEnter(e) {
      this.isHovering = true;
      if (e) {
        this.lastClientX = e.clientX;
        this.lastClientY = e.clientY;
      }
      await this.loadNaturalSize();
      if (!this.naturalWidth || !this.isHovering) return;

      this.ensureLens();
      this.ensurePane('side');
      this.positionSidePane();

      requestAnimationFrame(() => {
        if (!this.pane || !this.lens) return;
        this.pane.classList.add('is-visible');
        this.lens.classList.add('is-visible');
      });

      this.addEventListener('mousemove', this.onMouseMove);
      window.addEventListener('scroll', this.queueReposition, { passive: true });
      window.addEventListener('resize', this.queueReposition);
    }

    onMouseMove(e) {
      this.lastClientX = e.clientX;
      this.lastClientY = e.clientY;
      this.rect = this.getBoundingClientRect();
      this.updateZoom(e.clientX - this.rect.left, e.clientY - this.rect.top);
    }

    onMouseLeave() {
      this.isHovering = false;
      this.removeEventListener('mousemove', this.onMouseMove);
      window.removeEventListener('scroll', this.queueReposition);
      window.removeEventListener('resize', this.queueReposition);
      if (this.pane) this.pane.classList.remove('is-visible');
      if (this.lens) this.lens.classList.remove('is-visible');
    }

    queueReposition() {
      if (this.repositionQueued) return;
      this.repositionQueued = true;
      requestAnimationFrame(() => {
        this.repositionQueued = false;

        // Scrolling moves the image out from under the cursor without ever firing a
        // native mouseleave (the mouse itself never moved), which otherwise leaves
        // the pane stuck open over a slide that's no longer under the pointer.
        if (this.isHovering && typeof this.lastClientX === 'number') {
          const rect = this.getBoundingClientRect();
          const stillInside = this.lastClientX >= rect.left && this.lastClientX <= rect.right &&
            this.lastClientY >= rect.top && this.lastClientY <= rect.bottom;
          if (!stillInside) {
            this.onMouseLeave();
            return;
          }
        }

        if (this.pane && this.paneMode === 'side') this.positionSidePane();
      });
    }

    positionSidePane() {
      const rect = this.getBoundingClientRect();
      const gap = 16;
      const edge = 8;
      const spaceRight = window.innerWidth - rect.right - gap - edge;
      const targetPaneSize = 640;
      const minPaneSize = 360;
      const maxVerticalSpace = window.innerHeight - edge * 2;

      const size = Math.max(minPaneSize, Math.min(targetPaneSize, spaceRight, maxVerticalSpace));

      let left = rect.right + gap;
      left = Math.min(Math.max(left, edge), window.innerWidth - size - edge);

      // Vertical position tracks the viewport, not the image, so it stays centered on screen.
      const top = Math.min(Math.max((window.innerHeight - size) / 2, edge), Math.max(window.innerHeight - size - edge, edge));

      this.pane.style.width = `${size}px`;
      this.pane.style.height = `${size}px`;
      this.pane.style.left = `${left}px`;
      this.pane.style.top = `${top}px`;

      this.paneWidth = size;
      this.paneHeight = size;
      this.rect = rect;
    }

    /* ---------------- Mobile (long-press) ---------------- */

    onTouchStart(e) {
      if (e.touches.length > 1) return;

      const touch = e.touches[0];
      this.touchStartPoint = { x: touch.clientX, y: touch.clientY };

      clearTimeout(this.longPressTimer);
      this.addEventListener('touchmove', this.onTouchMoveWhilePending, { passive: true });

      this.longPressTimer = setTimeout(() => {
        this.removeEventListener('touchmove', this.onTouchMoveWhilePending);
        this.activateTouchZoom(touch.clientX, touch.clientY);
      }, 450);
    }

    onTouchMoveWhilePending(e) {
      const touch = e.touches[0];
      const dx = touch.clientX - this.touchStartPoint.x;
      const dy = touch.clientY - this.touchStartPoint.y;

      if (Math.sqrt(dx * dx + dy * dy) > 10) {
        clearTimeout(this.longPressTimer);
        this.removeEventListener('touchmove', this.onTouchMoveWhilePending);
      }
    }

    async activateTouchZoom(clientX, clientY) {
      await this.loadNaturalSize();
      if (!this.naturalWidth) return;

      this.touchActive = true;
      this.rect = this.getBoundingClientRect();
      this.ensurePane('overlay');
      this.paneWidth = this.rect.width;
      this.paneHeight = this.rect.height;
      this.pane.classList.add('is-visible');
      this.updateZoom(clientX - this.rect.left, clientY - this.rect.top);

      if (window.navigator.vibrate) window.navigator.vibrate(10);

      this.addEventListener('touchmove', this.onTouchMove, { passive: false });
    }

    onTouchMove(e) {
      if (!this.touchActive) return;
      e.preventDefault();
      const touch = e.touches[0];
      this.updateZoom(touch.clientX - this.rect.left, touch.clientY - this.rect.top);
    }

    onTouchEnd() {
      clearTimeout(this.longPressTimer);
      this.removeEventListener('touchmove', this.onTouchMoveWhilePending);

      if (this.touchActive) {
        this.touchActive = false;
        this.removeEventListener('touchmove', this.onTouchMove);
        if (this.pane) this.pane.classList.remove('is-visible');
      }
    }

    /* ---------------- Shared ---------------- */

    updateZoom(x, y) {
      const rect = this.rect || this.getBoundingClientRect();
      const clampedX = Math.min(Math.max(x, 0), rect.width);
      const clampedY = Math.min(Math.max(y, 0), rect.height);
      const ratioX = rect.width ? clampedX / rect.width : 0;
      const ratioY = rect.height ? clampedY / rect.height : 0;

      if (this.lens && this.paneMode === 'side') {
        const lensWidth = rect.width * (this.paneWidth / this.naturalWidth);
        const lensHeight = rect.height * (this.paneHeight / this.naturalHeight);
        let lensX = clampedX - lensWidth / 2;
        let lensY = clampedY - lensHeight / 2;
        lensX = Math.min(Math.max(lensX, 0), Math.max(rect.width - lensWidth, 0));
        lensY = Math.min(Math.max(lensY, 0), Math.max(rect.height - lensHeight, 0));

        this.lens.style.width = `${lensWidth}px`;
        this.lens.style.height = `${lensHeight}px`;
        this.lens.style.left = `${lensX}px`;
        this.lens.style.top = `${lensY}px`;
      }

      let bgX = ratioX * this.naturalWidth - this.paneWidth / 2;
      let bgY = ratioY * this.naturalHeight - this.paneHeight / 2;
      bgX = Math.min(Math.max(bgX, 0), Math.max(this.naturalWidth - this.paneWidth, 0));
      bgY = Math.min(Math.max(bgY, 0), Math.max(this.naturalHeight - this.paneHeight, 0));

      this.pane.style.backgroundPosition = `-${bgX}px -${bgY}px`;
    }

    teardown() {
      clearTimeout(this.longPressTimer);
      if (this.visibilityObserver) this.visibilityObserver.disconnect();
      this.removeEventListener('mousemove', this.onMouseMove);
      this.removeEventListener('touchmove', this.onTouchMoveWhilePending);
      this.removeEventListener('touchmove', this.onTouchMove);
      window.removeEventListener('scroll', this.queueReposition);
      window.removeEventListener('resize', this.queueReposition);

      if (this.pane) {
        this.pane.remove();
        this.pane = null;
      }
      if (this.lens) {
        this.lens.remove();
        this.lens = null;
      }
    }
  }

  customElements.define('product-superzoom', ProductSuperzoom);
}

